import React, { createContext, useContext, useState, useRef, useCallback, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Info, AlertTriangle, CheckCircle2, XCircle, HelpCircle } from 'lucide-react';
import './MessageBox.scss';

export interface ShowMessageBoxOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'info' | 'confirm' | 'warning' | 'error' | 'success';
}

interface MessageBoxContextType {
  showMessageBox: (options: ShowMessageBoxOptions) => Promise<boolean>;
}

const MessageBoxContext = createContext<MessageBoxContextType | undefined>(undefined);

export const useMessageBox = () => {
  const context = useContext(MessageBoxContext);
  if (!context) {
    throw new Error('useMessageBox must be used within a MessageBoxProvider');
  }
  return context;
};

export const MessageBoxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ShowMessageBoxOptions | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const showMessageBox = useCallback((opts: ShowMessageBoxOptions): Promise<boolean> => {
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
    setOptions(opts);
    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    setOptions(null);
    if (resolveRef.current) {
      resolveRef.current(true);
      resolveRef.current = null;
    }
  }, []);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    setOptions(null);
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      handleCancel();
    }
  }, [handleCancel]);

  const renderIcon = () => {
    const type = options?.type || 'confirm';
    const size = 36;
    switch (type) {
      case 'info':
        return <Info size={size} className="text-blue-400" />;
      case 'success':
        return <CheckCircle2 size={size} className="text-emerald-400" />;
      case 'warning':
        return <AlertTriangle size={size} className="text-amber-400" />;
      case 'error':
        return <XCircle size={size} className="text-red-500" />;
      case 'confirm':
      default:
        return <HelpCircle size={size} className="text-[#c72f38]" />;
    }
  };

  const title = options?.title || (options?.type === 'info' ? 'Information' : options?.type === 'success' ? 'Success' : options?.type === 'error' ? 'Error' : options?.type === 'warning' ? 'Warning' : 'Confirm');
  const confirmText = options?.confirmText || 'OK';
  const cancelText = options?.cancelText || 'Cancel';
  const isConfirmType = options?.type === 'confirm' || !options?.type || options?.type === 'warning';

  const value = useMemo(() => ({ showMessageBox }), [showMessageBox]);

  return (
    <MessageBoxContext.Provider value={value}>
      {children}
      
      <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="message-box-overlay fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999]" />
          <Dialog.Content className="message-box-content fixed top-1/2 left-1/2 w-[90%] max-w-sm rounded-2xl p-6 shadow-2xl z-[10000] text-white backdrop-blur-md focus:outline-none flex flex-col items-center text-center">
            <div className="mb-4 message-box-icon-wrapper p-3 bg-white/5 rounded-full border border-white/10 flex items-center justify-center">
              {renderIcon()}
            </div>
            
            <Dialog.Title className="text-lg font-black tracking-tight mb-2 uppercase text-white font-google-sans">
              {title}
            </Dialog.Title>
            
            <Dialog.Description className="text-sm text-slate-300 font-medium leading-relaxed mb-6 whitespace-pre-line font-google-sans">
              {options?.message}
            </Dialog.Description>
            
            <div className="flex gap-3 w-full justify-center">
              {isConfirmType && (
                <button
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 font-semibold text-sm transition-all focus:outline-none focus:ring-2 focus:ring-white/20 active:scale-[0.98] font-google-sans cursor-pointer"
                >
                  {cancelText}
                </button>
              )}
              <button
                onClick={handleConfirm}
                className={`px-4 py-2.5 rounded-lg text-white font-semibold text-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#c72f38]/50 active:scale-[0.98] font-google-sans cursor-pointer ${isConfirmType ? 'flex-1 bg-[#c72f38] hover:bg-[#b02830]' : 'w-full max-w-[200px] bg-[#c72f38] hover:bg-[#b02830]'}`}
              >
                {confirmText}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </MessageBoxContext.Provider>
  );
};
